import {
  ColumnTypes,
  editableComponents,
  IEditableCellProps,
} from "@/containers/ProjectTemplate";
import { nameof } from "@/helpers";
import {
  IFormFieldOptions,
  IMilestoneTemplate,
  INotificationGroup,
} from "@/interfaces";
import {
  useGetAllNotificationGroupsQuery,
  useGetFormLookupsQuery,
} from "@/services";
import { FieldType } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPlusCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  updateMilestoneTemplate(data: Partial<IMilestoneTemplate>): void;
}

export const TemplateNotificationGroups = ({
  milestoneTemplate,
  updateMilestoneTemplate,
}: IProps) => {
  const intl = useIntl();

  const { data: { intake } = {} } = useGetFormLookupsQuery();

  const [notificationGroupsSuggestions, setNotificationGroupsSuggestions] =
    useState<IFormFieldOptions>({});
  const [notificationGroups, setNotificationGroups] = useState<
    INotificationGroup[]
  >([]);

  const { data: notificationGroupIds } = useGetAllNotificationGroupsQuery({});

  useEffect(() => {
    setNotificationGroups(milestoneTemplate.notification?.groups);
  }, [milestoneTemplate?.notification]);

  useEffect(() => {
    const dictionary: IFormFieldOptions = {};

    notificationGroupIds?.forEach(
      ({ id, name }: { id: string; name: string }) => {
        dictionary[id] = { id: "", fallback: name };
      }
    );

    setNotificationGroupsSuggestions(dictionary);
  }, [notificationGroupIds]);

  const updateModalState = (updatedGroups: INotificationGroup[]): void => {
    setNotificationGroups(updatedGroups);
    updateMilestoneTemplate({
      notification: {
        ...milestoneTemplate.notification,
        groups: updatedGroups,
      },
    });
  };

  const handleAdd = (): void => {
    const newGroup = [
      {
        id: v4(),
      } as INotificationGroup,
    ];

    const updatedGroups = newGroup.concat(notificationGroups);

    updateModalState(updatedGroups);
  };

  const handleDelete = (id: string): void => {
    const updatedGroups = notificationGroups.filter((group) => group.id !== id);
    setNotificationGroups(updatedGroups);

    updateModalState(updatedGroups);
  };

  const handleUpdate = (row: INotificationGroup): void => {
    const updatedGroups = [...notificationGroups];
    const index = updatedGroups.findIndex((group) => row.id === group.id);
    const item = updatedGroups[index];

    updatedGroups.splice(index, 1, {
      ...item,
      ...row,
    });

    updateModalState(updatedGroups);
  };

  const defaultColumns: (ColumnTypes[number] & {
    type: FieldType | undefined;
    dataIndex: string;
    required?: boolean;
    editable?: boolean;
    options?: IFormFieldOptions;
    placeholder?: string;
    showSearch?: boolean;
  })[] = [
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.group" }),
      dataIndex: nameof<INotificationGroup>("groupId"),
      key: "group",
      width: "350px",
      type: "DropDown",
      required: true,
      editable: true,
      showSearch: true,
      options: notificationGroupsSuggestions,
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.productCategory",
      }),
      dataIndex: nameof<INotificationGroup>("defaultForProductCategories"),
      key: "productCategory",
      width: "750px",
      fixed: true,
      type: "MultiSelectDropDown",
      editable: true,
      options: intake?.productCategory ?? {},
      placeholder: intl.formatMessage({ id: "general.all" }),
    },
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.required" }),
      dataIndex: nameof<INotificationGroup>("required"),
      key: "required",
      width: "85px",
      type: "Checkbox",
      editable: true,
    },
    {
      title: (
        <Button
          data-test-id="milestone-template-notification-groups-add"
          type="link"
          onClick={handleAdd}
          icon={<FontAwesomeIcon icon={faPlusCircle as IconProp} size="xl" />}
        />
      ),
      dataIndex: nameof<INotificationGroup>("id"),
      key: "delete",
      type: undefined,
      editable: false,
      render: (id: string) => (
        <Button
          data-test-id="milestone-template-notification-groups-delete"
          type="link"
          onClick={(): void => handleDelete(id)}
          icon={<FontAwesomeIcon icon={faTrash as IconProp} color="red" />}
        />
      ),
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: INotificationGroup) =>
        ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          type: col.type,
          required: col.required,
          options: col.options,
          showSearch: col.showSearch,
          handleSave: handleUpdate,
          placeholder: col.placeholder,
        } as IEditableCellProps<INotificationGroup>),
    };
  });

  return (
    <Table
      components={editableComponents}
      style={{ margin: "-12px 0px" }}
      tableLayout="fixed"
      className="w-full"
      size="small"
      columns={columns as ColumnTypes}
      dataSource={notificationGroups}
      rowKey="id"
      pagination={false}
    />
  );
};
